import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { DASHBOARD_TABS, DASHBOARD_PANELS } from "@/components/dashboard/DashboardTabs";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "ads";

  const [mine, setMine] = useState([]);
  const [saved, setSaved] = useState([]);
  const [convos, setConvos] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [reply, setReply] = useState("");
  const [searches, setSearches] = useState([]);
  const [payments, setPayments] = useState([]);
  const [following, setFollowing] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [notif, setNotif] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [topupAmt, setTopupAmt] = useState("20");

  useEffect(() => { if (!loading && !user) navigate("/"); }, [user, loading, navigate]);

  const loadAll = useCallback(() => {
    api.get("/listings/mine").then((r) => setMine(r.data));
    api.get("/favorites").then((r) => setSaved(r.data));
    api.get("/messages/conversations").then((r) => setConvos(r.data));
    api.get("/saved-searches").then((r) => setSearches(r.data)).catch(() => {});
    api.get("/payments/history").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/following").then((r) => setFollowing(r.data)).catch(() => {});
    api.get("/blocked").then((r) => setBlocked(r.data)).catch(() => {});
    api.get("/notifications/settings").then((r) => setNotif(r.data)).catch(() => {});
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  const runSearch = (query) => navigate(`/search?${new URLSearchParams(query || {}).toString()}`);
  const deleteSearch = async (id) => {
    await api.delete(`/saved-searches/${id}`);
    setSearches((s) => s.filter((x) => x.id !== id));
    toast.success("Saved search removed");
  };
  const unfollow = async (sid) => {
    await api.delete(`/sellers/${sid}/follow`);
    setFollowing((f) => f.filter((x) => x.seller_id !== sid));
    toast.success("Unfollowed seller");
  };
  const unblock = async (uid) => {
    await api.delete(`/users/${uid}/block`);
    setBlocked((b) => b.filter((x) => x.user_id !== uid));
    toast.success("User unblocked");
  };
  const saveNotif = async (key, val) => {
    const next = { ...notif, [key]: val };
    setNotif(next);
    try { await api.put("/notifications/settings", next); toast.success("Notification settings saved"); }
    catch { toast.error("Could not save settings"); }
  };
  const topup = async () => {
    const amount = Number(topupAmt);
    if (!amount || amount < 5) { toast.error("Minimum top-up is €5"); return; }
    try {
      const { data } = await api.post("/wallet/topup", { amount, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start top-up"); }
  };

  const del = async (id) => {
    await api.delete(`/listings/${id}`);
    toast.success("Listing deleted");
    setMine((m) => m.filter((l) => l.id !== id));
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeConvo) return;
    await api.post("/messages/reply", { convo_key: activeConvo.convo_key, text: reply });
    setReply("");
    const { data } = await api.get("/messages/conversations");
    setConvos(data);
    setActiveConvo(data.find((c) => c.convo_key === activeConvo.convo_key));
  };

  const completePayment = async (id) => {
    try {
      const { data } = await api.post("/payments/checkout-listing", { listing_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
    }
  };

  const markSold = async (id) => {
    await api.post(`/listings/${id}/mark-sold`);
    toast.success("Nice one, marked as sold");
    setMine((m) => m.map((l) => (l.id === id ? { ...l, status: "sold" } : l)));
  };

  const relist = async (id) => {
    await api.post(`/listings/${id}/relist`);
    toast.success("Back up and live again");
    setMine((m) => m.map((l) => (l.id === id ? { ...l, status: "active" } : l)));
  };

  if (!user) return <div className="min-h-screen"><Header /></div>;

  const active = mine.filter((l) => l.status === "active");
  const drafts = mine.filter((l) => l.status === "draft");
  const pending = mine.filter((l) => l.status === "pending_payment");
  const sold = mine.filter((l) => l.status === "sold");

  const Panel = DASHBOARD_PANELS[tab] || DASHBOARD_PANELS.ads;
  const ctx = {
    mine, saved, convos, activeConvo, setActiveConvo, reply, setReply, searches, payments,
    following, blocked, notif, wallet, topupAmt, setTopupAmt, navigate,
    active, drafts, pending, sold,
    del, sendReply, completePayment, markSold, relist, runSearch, deleteSearch,
    unfollow, unblock, saveNotif, topup,
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full flex-1">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-3xl font-bold text-slate-900">My DealShed</h1>
          <Button onClick={() => navigate("/place-ad")} data-testid="dashboard-place-ad" className="bg-amber-500 hover:bg-amber-600 text-white"><Plus size={16} className="mr-1" /> Place Ad</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1">
            <nav className="bg-white border border-slate-200 rounded-lg p-2 space-y-1 sticky top-20">
              {DASHBOARD_TABS.map((t) => (
                <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setParams({ tab: t.k })}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${tab === t.k ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"}`}>
                  <t.icon size={16} /> {t.label}
                </button>
              ))}
            </nav>
          </aside>

          <main className="lg:col-span-3">
            <Panel ctx={ctx} />
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
